import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useLabTests, useDeleteLabTest } from "api/tests";
import React, { useState } from "react";
import { FaPlus } from "react-icons/fa";
import EditIcon from "../../assets/svg/EditIcon";
import DeleteIcon from "../../assets/svg/DeleteIcon";
import ToolbarPortal from "generics/ToolbarPortal";
import PageTitle from "generics/PageTitle";
import { useNavigate } from "react-router";
import AddTest from "./AddTest";
import { useDebouncedCallback } from "hooks/useDebouncedCallback";
import { SearchInput } from "generics/SearchInput";

export default function Tests() {
  const labTestsQuery = useLabTests();
  const [selectedTest, setSelectedTest] = useState();
  const [action, setAction] = useState();
  const addTest = useDisclosure();

  const [filterTerm, setFilterTerm] = useState("");
  const debouncedOnChange = useDebouncedCallback(
    (e) => setFilterTerm(e.target.value),
    400
  );

  return (
    <Box margin={8}>
      <ToolbarPortal>
        <Flex justifyContent="space-between" alignItems="center">
          <Flex justify="space-between" flexGrow={1} align="center">
            <PageTitle flexGrow={1}>Tests</PageTitle>

            <Stack direction="row" alignItems="stretch">
              <SearchInput onChange={(e) => debouncedOnChange(e)} />
              <Button
                onClick={addTest.onOpen}
                color="white"
                align="center"
                leftIcon={<FaPlus></FaPlus>}
              >
                Add Tests
              </Button>
            </Stack>
          </Flex>
        </Flex>
      </ToolbarPortal>

      <DeleteTestModal
        selectedTest={selectedTest}
        action={action}
        onClose={() => {
          setSelectedTest(undefined);
          setAction(undefined);
        }}
      ></DeleteTestModal>

      <AddTestModal
        onClose={addTest.onClose}
        isOpen={addTest.isOpen}
      ></AddTestModal>
      {labTestsQuery.isLoading ? (
        <Box h="300px">
          <Center w="100%" h="100%">
            <Spinner size="lg" />
          </Center>
        </Box>
      ) : labTestsQuery.data?.length === 0 ? (
        <Center h="200px" w="100%">
          <Text fontSize="xl" color="gray.500">
            No Tests!
          </Text>
        </Center>
      ) : (
        <Stack py={2}>
          <Table>
            <Thead
              fontSize="16px"
              fontWeight="600"
              borderBottom="1px solid #EBECF2"
            >
              <Tr>
                <Td>NAME</Td>
                <Td>DESCRIPTION</Td>
                <Td>PRICE</Td>
                <Td>ACTIONS </Td>
              </Tr>
            </Thead>
            <Tbody>
              {(
                labTestsQuery.data?.filter((test: any) =>
                  test.name.toLowerCase().includes(filterTerm.toLowerCase())
                ) || []
              ).map((test: any) => {
                return (
                  <LabTestItem
                    test={test}
                    key={test.id}
                    setSelectedTest={setSelectedTest}
                    setAction={setAction}
                  ></LabTestItem>
                );
              })}
            </Tbody>
          </Table>
        </Stack>
      )}
    </Box>
  );
}

function DeleteTestModal({ onClose, selectedTest: test, action }: any) {
  const [deleteLabTest, deleteLabTestInfo] = useDeleteLabTest();

  const cancelRef = React.useRef<any>();
  async function onSubmit() {
    await deleteLabTest(test.id);
    onClose();
  }
  return (
    <AlertDialog
      motionPreset="slideInBottom"
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isOpen={test && action === "delete"}
    >
      <AlertDialogOverlay />
      <AlertDialogContent maxW="50vw">
        <Flex direction="column">
          <AlertDialogHeader bg="white" color="black" borderTopRadius="4px">
            Delete {test?.name}
          </AlertDialogHeader>
          <AlertDialogCloseButton color="richText.500" fontSize="14" />
          <Divider color="border.500" height="2px"></Divider>
          <AlertDialogBody marginY={4}>
            <Text size="sm" color="settingText.500">
              Are you sure you want to delete this test. Once you delete it you
              will not be able to get it back.
            </Text>
          </AlertDialogBody>
          <Divider></Divider>
          <AlertDialogFooter>
            <Button
              bg="red.300"
              onClick={onSubmit}
              isLoading={deleteLabTestInfo.isLoading}
            >
              Delete
            </Button>
            <Button
              ml={3}
              variant="outline"
              border="2px"
              borderColor="border.500"
              ref={cancelRef}
              onClick={onClose}
            >
              Cancel
            </Button>
          </AlertDialogFooter>
        </Flex>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AddTestModal({ isOpen, onOpen, onClose }: any) {
  const cancelRef = React.useRef<any>();
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay></AlertDialogOverlay>
      <AlertDialogContent maxW="50vw" minW="512px">
        <AlertDialogHeader
          bg="modalHeader.500"
          color="white"
          borderTopRadius="4px"
        >
          Add New Test
        </AlertDialogHeader>
        <AlertDialogCloseButton
          color="richText.500"
          fontSize="14"
        ></AlertDialogCloseButton>
        <AlertDialogBody>
          <AddTest onCancel={onClose}></AddTest>
        </AlertDialogBody>
        <Divider></Divider>
      </AlertDialogContent>
    </AlertDialog>
  );
}
function LabTestItem({
  test,
  setSelectedTest,
  setAction,
}: {
  test: any;
  setSelectedTest: any;
  setAction: any;
}) {
  const navigate = useNavigate();
  return (
    <>
      <Tr textAlign="center" verticalAlign="top">
        <Td align="char">
          <Text fontSize="sm" color="settingText.500" fontWeight="500">
            {test.name}
          </Text>
        </Td>
        <Td>
          <Text fontSize="sm" color="settingText.500" fontWeight="400">
            {test.description}
          </Text>
        </Td>
        <Td>
          <Text fontSize="sm" color="settingText.500" fontWeight="400">
            Rs.{test.fee}
          </Text>
        </Td>
        <Td>
          <HStack>
            <IconButton
              size="sm"
              bg="transparent"
              color="black"
              icon={<EditIcon />}
              aria-label="edit-test"
              onClick={() => {
                navigate(`/tests/${test.id}`);
              }}
            ></IconButton>
            <IconButton
              bg="transparent"
              color="red.300"
              icon={<DeleteIcon />}
              aria-label="delete-test"
              size="sm"
              onClick={() => {
                setAction("delete");
                setSelectedTest(test);
              }}
            ></IconButton>
          </HStack>
        </Td>
      </Tr>
    </>
  );
}
